<template>
  <LISAPopover placement="bottom-end" role="dialog">
    <template #trigger="{ triggerAttrs }">
      <LISAButton
        v-bind="triggerAttrs"
        size="sm"
        text
        icon="fas fa-save"
        :label="t('global.save')"
        :aria-label="t('global.save')"
      />
    </template>

    <section class="saved-filter-manager">
      <h4 class="saved-filter-manager__title">
        {{ t("components.event-action.filter.FilterUserQuickActions.MyFilter") }}
      </h4>

      <div v-if="savedFilters.length > 0" class="saved-filter-manager__list">
        <div v-for="group in filterGroups" :key="group.scope">
          <article
            v-for="filter in group.filters"
            :key="filter.id"
            class="saved-filter-manager__row"
            @dragover="allowDrop(group.scope, $event)"
            @drop="dropFilter(group, filter.id)"
          >
            <i
              v-if="canReorder(group.scope)"
              class="saved-filter-manager__drag-handle fas fa-grip-vertical"
              draggable="true"
              aria-hidden="true"
              @dragstart="startReorder(group.scope, filter.id, $event)"
              @dragend="cancelReorder"
            />
            <span v-else />
            <span class="saved-filter-manager__badge">{{ filter.abbreviation }}</span>
            <span class="saved-filter-manager__summary">
              <strong>{{ filter.subtitle }}</strong>
              <small>
                {{
                  filter.boardId == null
                    ? t("components.event-action.filter.FilterUserQuickActions.MyFilter")
                    : t("global.board")
                }}
              </small>
            </span>
            <LISAButton
              v-if="canManage(filter)"
              size="sm"
              text
              variant="neutral"
              icon="fas fa-pencil"
              :aria-label="t('global.edit')"
              @click="editFilter(filter)"
            />
            <LISAButton
              v-if="canManage(filter)"
              size="sm"
              text
              variant="error"
              icon="fas fa-trash"
              :aria-label="t('global.delete')"
              :loading="deletingId === filter.id"
              @click="deleteFilter(filter.id)"
            />
          </article>
        </div>
      </div>

      <form class="saved-filter-manager__form" @submit.prevent="saveFilter">
        <div class="saved-filter-manager__fields">
          <label>
            <span>{{ t("modules.flags.components.FlagComponent.abbreviation") }}</span>
            <LISAInput v-model="abbreviation" maxlength="5" required />
          </label>
          <label>
            <span>{{ t("global.description") }}</span>
            <LISAInput v-model="subtitle" maxlength="255" required />
          </label>
        </div>

        <div v-if="canSaveForBoard" class="saved-filter-manager__scope">
          <label>
            <input v-model="scope" type="radio" value="personal" />
            {{ t("components.event-action.filter.FilterUserQuickActions.MyFilter") }}
          </label>
          <label>
            <input v-model="scope" type="radio" value="board" />
            {{ t("global.board") }}
          </label>
        </div>

        <div class="saved-filter-manager__actions">
          <LISAButton
            v-if="editingFilter != null"
            type="button"
            size="sm"
            text
            variant="neutral"
            :label="t('global.cancel')"
            @click="resetForm"
          />
          <LISAButton
            type="submit"
            size="sm"
            icon="fas fa-save"
            :label="t('global.save')"
            :loading="saving"
            :disabled="!canSubmit"
          />
        </div>
      </form>
    </section>
  </LISAPopover>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { LISAButton, LISAInput, LISAPopover } from "@fi-solutions/lisa-ui";
import {
  type FullEventActionBoardFilterFragment,
  useDeleteEventActionBoardFilterMutation,
  useEventActionBoardFiltersQuery,
  useReorderEventActionBoardFiltersMutation,
  useSaveEventActionBoardFilterMutation,
} from "@/graphql";
import { useTranslation } from "@/language/translation";

type SerializedFilter = {
  type: number;
  className: string;
  values: unknown;
};

const props = defineProps<{
  boardId?: number;
  filters: SerializedFilter[];
}>();

const { t } = useTranslation();
const store = useStore();
const hasSQDCAdmin = computed(() => store.getters["login/hasAccessRight"]("sqdc.admin"));
const canSaveForBoard = computed(() => props.boardId != null && hasSQDCAdmin.value);

const { result } = useEventActionBoardFiltersQuery(() => ({ boardId: props.boardId }), {
  fetchPolicy: "cache-and-network",
});
const savedFilters = computed(() => result.value?.EventActionBoardFilters ?? []);
const boardFilters = ref<FullEventActionBoardFilterFragment[]>([]);
const personalFilters = ref<FullEventActionBoardFilterFragment[]>([]);
watch(
  savedFilters,
  (filters) => {
    boardFilters.value = filters.filter(({ boardId }) => boardId != null);
    personalFilters.value = filters.filter(({ boardId }) => boardId == null);
  },
  { immediate: true },
);

type FilterScope = "board" | "personal";
type FilterGroup = {
  scope: FilterScope;
  filters: FullEventActionBoardFilterFragment[];
};
const filterGroups = computed<FilterGroup[]>(() => [
  { scope: "board", filters: boardFilters.value },
  { scope: "personal", filters: personalFilters.value },
]);
const draggedFilter = ref<{ scope: FilterScope; id: number } | null>(null);

const abbreviation = ref("");
const subtitle = ref("");
const scope = ref<"personal" | "board">("personal");
const editingFilter = ref<FullEventActionBoardFilterFragment | null>(null);
const deletingId = ref<number | null>(null);

const { mutate: save, loading: saving } = useSaveEventActionBoardFilterMutation({
  refetchQueries: ["EventActionBoardFilters"],
});
const { mutate: remove } = useDeleteEventActionBoardFilterMutation({
  refetchQueries: ["EventActionBoardFilters"],
});
const { mutate: reorder } = useReorderEventActionBoardFiltersMutation({
  refetchQueries: ["EventActionBoardFilters"],
});

const canSubmit = computed(
  () =>
    abbreviation.value.trim().length > 0 &&
    subtitle.value.trim().length > 0 &&
    (editingFilter.value != null || props.filters.length > 0),
);

function canManage(filter: FullEventActionBoardFilterFragment) {
  return filter.boardId == null || hasSQDCAdmin.value;
}

function canReorder(filterScope: FilterScope) {
  return filterScope === "personal" || hasSQDCAdmin.value;
}

async function reorderFilters(group: FilterGroup) {
  if (!canReorder(group.scope)) {
    return;
  }
  await reorder({
    ids: group.filters.map(({ id }) => id),
    boardId: group.scope === "board" ? props.boardId : undefined,
  });
}

function startReorder(filterScope: FilterScope, id: number, event: DragEvent) {
  draggedFilter.value = { scope: filterScope, id };
  if (event.dataTransfer != null) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(id));
  }
}

function allowDrop(filterScope: FilterScope, event: DragEvent) {
  if (draggedFilter.value?.scope !== filterScope) {
    return;
  }
  event.preventDefault();
  if (event.dataTransfer != null) {
    event.dataTransfer.dropEffect = "move";
  }
}

async function dropFilter(group: FilterGroup, targetId: number) {
  const dragged = draggedFilter.value;
  draggedFilter.value = null;
  if (dragged == null || dragged.scope !== group.scope || dragged.id === targetId) {
    return;
  }
  const sourceIndex = group.filters.findIndex(({ id }) => id === dragged.id);
  const targetIndex = group.filters.findIndex(({ id }) => id === targetId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return;
  }
  const [filter] = group.filters.splice(sourceIndex, 1);
  group.filters.splice(targetIndex, 0, filter);
  await reorderFilters(group);
}

function cancelReorder() {
  draggedFilter.value = null;
}

function editFilter(filter: FullEventActionBoardFilterFragment) {
  editingFilter.value = filter;
  abbreviation.value = filter.abbreviation;
  subtitle.value = filter.subtitle;
  scope.value = filter.boardId == null ? "personal" : "board";
}

function resetForm() {
  editingFilter.value = null;
  abbreviation.value = "";
  subtitle.value = "";
  scope.value = "personal";
}

async function saveFilter() {
  if (!canSubmit.value) {
    return;
  }
  const storedFilters = editingFilter.value?.filters ?? props.filters;
  await save({
    filter: {
      id: editingFilter.value?.id,
      abbreviation: abbreviation.value.trim(),
      subtitle: subtitle.value.trim(),
      boardId: scope.value === "board" ? props.boardId : undefined,
      filters: storedFilters.map(({ type, className, values }) => ({ type, className, values })),
    },
  });
  resetForm();
}

async function deleteFilter(id: number) {
  deletingId.value = id;
  try {
    await remove({ id });
    if (editingFilter.value?.id === id) {
      resetForm();
    }
  } finally {
    deletingId.value = null;
  }
}
</script>

<style scoped lang="scss">
.saved-filter-manager {
  width: min(32rem, calc(100vw - 2rem));
  padding: 1rem;

  &__title {
    margin: 0 0 0.75rem;
  }

  &__list {
    display: grid;
    margin-bottom: 1rem;
  }

  &__row {
    display: grid;
    grid-template-columns: 1rem 3rem minmax(0, 1fr) auto auto;
    gap: 0.5rem;
    align-items: center;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--p-surface-200);
  }

  &__drag-handle {
    color: var(--p-text-muted-color);
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &__badge {
    display: grid;
    min-height: 2.5rem;
    place-items: center;
    padding: 0.25rem;
    color: var(--p-primary-700);
    font-weight: 700;
    background: var(--p-primary-100);
    border-radius: 0.5rem;
  }

  &__summary {
    display: grid;
    min-width: 0;

    strong,
    small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      color: var(--p-text-muted-color);
    }
  }

  &__form {
    display: grid;
    gap: 0.75rem;
  }

  &__fields {
    display: grid;
    grid-template-columns: minmax(6rem, 0.35fr) minmax(12rem, 1fr);
    gap: 0.75rem;

    label {
      display: grid;
      gap: 0.25rem;
    }
  }

  &__scope,
  &__actions {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  &__scope label {
    display: flex;
    gap: 0.375rem;
    align-items: center;
  }

  &__actions {
    justify-content: flex-end;
  }
}

@media (max-width: 36rem) {
  .saved-filter-manager__fields {
    grid-template-columns: 1fr;
  }
}
</style>
